import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";

import { adminGet } from "@lib/adminApi";
import { flagWording } from "@lib/usd";

import {
  CONCLUSION_COLOR,
  formatAge,
  formatCadence,
  formatChange,
  formatMoney,
  formatUsd,
  mispricingText,
  monitorTitle,
  STATE_LABEL,
  STATE_STYLE,
  weekdayIn,
} from "./healthFormat";
import { ErrorLine, Panel, REFRESH_MS, RefreshFailed } from "./panelParts";
import { PerfPanel } from "./PerfPanel";
import { PrioritiesPanel } from "./PrioritiesPanel";
import type {
  MonitorsResponse,
  MonitorStatus,
  PurchasesResponse,
} from "./types";

// The admin Health page (OMG-2767): is tracking healthy, do purchases look
// normal for this time of day, and are the marketing pages getting heavier?
// Monitors report themselves to omg-api at the end of every run; this page
// only reads. Refreshes every minute.

const HISTORY_SHOWN = 14;

const Bars = ({ points }: { points: { day: string; count: number }[] }) => {
  if (points.length === 0) return null;
  const max = Math.max(1, ...points.map((p) => p.count));
  const H = 40;
  const barW = 100 / points.length;
  return (
    <svg
      viewBox={`0 0 100 ${H}`}
      preserveAspectRatio="none"
      className="h-12 w-full text-cta"
      role="img"
      aria-label={`Purchases per day, last ${points.length} days`}
    >
      {points.map((p, i) => {
        const h = (p.count / max) * H;
        return (
          <rect
            key={p.day}
            x={i * barW + barW * 0.15}
            y={H - h}
            width={barW * 0.7}
            height={h}
            className={
              i === points.length - 1 ? "fill-gray-400" : "fill-current"
            }
          >
            <title>{`${p.day}: ${p.count}${i === points.length - 1 ? " (today so far)" : ""}`}</title>
          </rect>
        );
      })}
    </svg>
  );
};

const PurchasesPanel = () => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["admin-health-purchases"] as const,
    queryFn: () => adminGet<PurchasesResponse>("/admin/health/purchases"),
    refetchInterval: REFRESH_MS,
  });

  if (isLoading) return <Panel title="Purchases">Loading...</Panel>;
  if (!data)
    return (
      <Panel title="Purchases">
        <ErrorLine error={error} />
      </Panel>
    );

  const weekday = weekdayIn(data.timezone, new Date(data.generatedAt));
  const change = formatChange(data.changePercent);
  const changeColor =
    data.changePercent === null
      ? "text-gray-500"
      : data.changePercent <= -30
        ? "text-red-700"
        : data.changePercent < 0
          ? "text-amber-700"
          : "text-green-700";
  const attention = data.needsAttention;
  const attentionTotal =
    attention.abandoned +
    attention.stuck +
    attention.voidFailed +
    attention.settlementFailed;
  const avgByCurrency = new Map(
    data.baseline.averageRevenue.map((r) => [r.currency, r.amount]),
  );
  // A currency whose USD average sale is far from the US list price, today
  // or over the baseline weeks (OMG-2793): said loudly, once per currency.
  const flagged = [...data.today.revenue, ...data.baseline.averageRevenue]
    .filter((r) => r.usd?.flag)
    .filter(
      (r, i, all) => all.findIndex((x) => x.currency === r.currency) === i,
    );
  // The rates' date, from whichever line carries one (today may have sold
  // only in a currency without a rate while the baseline has the others).
  const ratesAsOf = [
    ...data.today.revenue,
    ...data.baseline.averageRevenue,
  ].find((r) => r.usd)?.usd?.rateDate;

  return (
    <Panel title="Purchases">
      {error && <RefreshFailed error={error} />}
      {flagged.length > 0 && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle
              className="mt-0.5 h-4 w-4 shrink-0 text-red-700"
              aria-hidden="true"
            />
            <div>
              <strong>Possible mispricing.</strong> The average sale in these
              currencies is far from the US list price of the same plans:
              <ul className="mt-1 list-disc pl-5 font-semibold">
                {flagged.map((r) => (
                  <li key={r.currency}>
                    {mispricingText(r.currency, r.usd!)} (
                    {flagWording(r.usd!.flag)})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <div className="text-xs text-gray-500">Today so far (Pacific)</div>
          <div className="text-3xl font-bold">
            {data.today.count.toLocaleString()}
          </div>
          <div className="text-xs text-gray-500">
            Average {data.baseline.averageCount} by this time on the last{" "}
            {data.baseline.weeks.length} {weekday}s
            {change && (
              <span className={`ml-1 font-semibold ${changeColor}`}>
                {change}
              </span>
            )}
          </div>
        </div>

        <div>
          <div
            className="text-xs text-gray-500"
            title={
              ratesAsOf
                ? `Average sale in US dollars at the newest daily rate (${ratesAsOf}), against the US list price of the plans sold`
                : "No USD exchange rates are loaded for these currencies yet"
            }
          >
            Gross revenue today, by currency
          </div>
          {data.today.revenue.length === 0 ? (
            <div className="text-sm text-gray-500 mt-1">None yet</div>
          ) : (
            <ul className="mt-1 space-y-0.5">
              {data.today.revenue.slice(0, 4).map((r) => {
                const bad = r.usd?.flag != null;
                // Absent from an API without OMG-2793.
                const count = r.count ?? 0;
                return (
                  <li
                    key={r.currency}
                    className={`text-sm ${bad ? "text-red-700" : ""}`}
                    title={
                      bad && r.usd?.expectedAverageOrder != null
                        ? `${r.usd.flag === "low" ? "Too low" : "Too high"}: ${mispricingText(r.currency, r.usd)}`
                        : r.usd
                          ? `${formatUsd(r.usd.revenue)} in total at the newest rate`
                          : `No USD rate for ${r.currency}`
                    }
                  >
                    <span className="font-semibold">
                      {formatMoney(r.currency, r.amount)}
                    </span>
                    <span
                      className={`text-xs ${bad ? "font-semibold" : "text-gray-500"}`}
                    >
                      {" "}
                      {count} sale{count === 1 ? "" : "s"}
                      {r.usd && `, ${formatUsd(r.usd.averageOrder)} avg USD`}
                      {bad &&
                        r.usd?.expectedAverageOrder != null &&
                        `, expect about ${formatUsd(r.usd.expectedAverageOrder)}`}
                    </span>
                    {!bad && avgByCurrency.has(r.currency) && (
                      <span className="text-xs text-gray-500">
                        {" "}
                        (usual{" "}
                        {formatMoney(
                          r.currency,
                          avgByCurrency.get(r.currency) as string,
                        )}
                        )
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div>
          <div className="text-xs text-gray-500">
            Transactions needing attention
          </div>
          <Link
            to="/transactions/needs-attention"
            className={`text-3xl font-bold ${attentionTotal > 0 ? "text-red-700" : "text-gray-900"}`}
          >
            {attentionTotal}
            {attention.hasMore ? "+" : ""}
          </Link>
          {attentionTotal > 0 && (
            <div className="text-xs text-gray-500">
              {[
                attention.abandoned && `${attention.abandoned} abandoned`,
                attention.stuck && `${attention.stuck} stuck`,
                attention.voidFailed && `${attention.voidFailed} void failed`,
                attention.settlementFailed &&
                  `${attention.settlementFailed} settlement failed`,
              ]
                .filter(Boolean)
                .join(", ")}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-gray-500">
            Purchases per day, last {data.daily.length} days
          </span>
          <span className="text-xs text-gray-400">
            today in gray, still counting
          </span>
        </div>
        <Bars points={data.daily} />
      </div>
    </Panel>
  );
};

const HistoryStrip = ({ history }: { history: MonitorStatus["history"] }) => {
  // The API sends newest first; draw oldest to newest, left to right.
  const shown = history.slice(0, HISTORY_SHOWN).reverse();
  return (
    <div className="flex gap-0.5" aria-label="Recent runs, oldest to newest">
      {shown.map((c, i) => (
        <span
          key={i}
          title={c}
          className={`inline-block w-2 h-4 rounded-sm ${CONCLUSION_COLOR[c]}`}
        />
      ))}
    </div>
  );
};

const MonitorsPanel = () => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["admin-health-monitors"] as const,
    queryFn: () => adminGet<MonitorsResponse>("/admin/health/monitors"),
    refetchInterval: REFRESH_MS,
  });

  if (isLoading) return <Panel title="Tracking monitors">Loading...</Panel>;
  if (!data)
    return (
      <Panel title="Tracking monitors">
        <ErrorLine error={error} />
      </Panel>
    );

  if (data.monitors.length === 0) {
    return (
      <Panel title="Tracking monitors">
        {error && <RefreshFailed error={error} />}
        <p className="text-sm text-gray-600">
          No monitor has reported yet. Monitors report at the end of each run
          once the marketing-tracking repository has the MONITOR_INGEST_URL
          variable and the MONITOR_INGEST_KEY secret, and omg-api has the same
          key.
        </p>
      </Panel>
    );
  }

  const failing = data.monitors.filter((m) => m.state === "failing").length;
  const stale = data.monitors.filter((m) => m.state === "stale").length;

  return (
    <Panel title="Tracking monitors">
      {error && <RefreshFailed error={error} />}
      <p className="text-sm mb-3">
        {failing === 0 && stale === 0 ? (
          <span className="text-green-700">
            All {data.monitors.length} monitors passed their latest run.
          </span>
        ) : (
          <span className="text-red-700 inline-flex items-center gap-1">
            <AlertTriangle size={16} />
            {[
              failing && `${failing} failing`,
              stale && `${stale} not reporting on schedule`,
            ]
              .filter(Boolean)
              .join(", ")}
          </span>
        )}
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b">
            <th className="py-2 pr-2">Status</th>
            <th className="py-2 pr-2">Monitor</th>
            <th className="py-2 pr-2">Last run</th>
            <th className="py-2 pr-2">Recent runs</th>
            <th className="py-2 pr-2">Summary</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {data.monitors.map((m) => (
            <tr
              key={`${m.source}/${m.monitor}`}
              className="border-b last:border-0 align-top"
            >
              <td className="py-2 pr-2">
                <span
                  className={`px-2 py-0.5 rounded text-xs font-semibold ${STATE_STYLE[m.state]}`}
                >
                  {STATE_LABEL[m.state]}
                </span>
              </td>
              <td className="py-2 pr-2">
                <div className="font-medium">{monitorTitle(m.monitor)}</div>
                <div className="text-xs text-gray-400">
                  {formatCadence(m.expectedGapHours) ?? m.source}
                </div>
              </td>
              <td className="py-2 pr-2 whitespace-nowrap">
                <div>{formatAge(m.ageHours)}</div>
                <div className="text-xs text-gray-500">
                  {m.latest.conclusion.replace("_", " ")}
                </div>
              </td>
              <td className="py-2 pr-2">
                <HistoryStrip history={m.history} />
              </td>
              <td className="py-2 pr-2 text-xs text-gray-600 max-w-md">
                {m.latest.summary ?? ""}
              </td>
              <td className="py-2">
                {m.latest.runUrl && (
                  <a
                    href={m.latest.runUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                  >
                    Run <ExternalLink size={12} />
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-400 mt-2">
        Runs on {data.branch} only. A monitor is stale when its last run is
        older than 1.5 times its longest usual gap between runs.
      </p>
    </Panel>
  );
};

export const HealthPage = () => {
  const queryClient = useQueryClient();

  return (
    <div className="max-w-6xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Health</h1>
        <button
          type="button"
          onClick={() => {
            void queryClient.invalidateQueries({
              queryKey: ["admin-health-purchases"],
            });
            void queryClient.invalidateQueries({
              queryKey: ["admin-health-monitors"],
            });
            void queryClient.invalidateQueries({
              queryKey: ["admin-health-perf"],
            });
            void queryClient.invalidateQueries({
              queryKey: ["admin-health-priorities"],
            });
          }}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>
      <PrioritiesPanel />
      <PurchasesPanel />
      <MonitorsPanel />
      <PerfPanel />
    </div>
  );
};

export default HealthPage;
